import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import gsap from "gsap";
import "../css/Navbar.css";
import "../css/NavbarFx.css";
import "../css/NavbarWood.css";
import { SITE } from "../data/siteConfig";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Products", to: "/products" },
  { label: "Contact", to: "/contact" },
];

const CONTACT_INFO = {
  phone: `${SITE.phone}`,
  phoneLink: `${SITE.phoneLink}`,
  whatsapp: `${SITE.whatsapp}`,
  whatsappLink: `${SITE.whatsappLink}`,
  email: "anappuramfurniture@gmail.com",
  emailLink: "mailto:anappuramfurniture@gmail.com",
  address: "Kinassery, Palakkad, Kerala · Orders from all India",
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);
  const lastY = useRef(0);

  // Solid background + hide on scroll down / show on scroll up
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      if (y > 240 && y > lastY.current + 6) setHidden(true);
      else if (y < lastY.current - 6 || y <= 240) setHidden(false);
      lastY.current = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setHidden(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Intro + magnetic call button (desktop only)
  useEffect(() => {
    const mm = gsap.matchMedia();
    mm.add(
      "(min-width: 901px) and (prefers-reduced-motion: no-preference)",
      () => {
        const header = headerRef.current;
        gsap.from(header.querySelector(".navbar__logo"), {
          y: -30,
          opacity: 0,
          duration: 0.9,
          ease: "power3.out",
          delay: 0.2,
          clearProps: "transform,opacity",
        });
        gsap.from(header.querySelectorAll(".navbar__links li"), {
          y: -24,
          opacity: 0,
          duration: 0.8,
          stagger: 0.09,
          ease: "power3.out",
          delay: 0.35,
          clearProps: "transform,opacity",
        });
        gsap.from(header.querySelector(".navbar__call-btn"), {
          scale: 0.8,
          opacity: 0,
          duration: 0.8,
          ease: "back.out(1.8)",
          delay: 0.8,
          clearProps: "transform,opacity",
        });

        const btn = header.querySelector(".navbar__call-btn");
        const qx = gsap.quickTo(btn, "x", { duration: 0.4, ease: "power3" });
        const qy = gsap.quickTo(btn, "y", { duration: 0.4, ease: "power3" });
        const move = (e) => {
          const r = btn.getBoundingClientRect();
          qx((e.clientX - (r.left + r.width / 2)) * 0.3);
          qy((e.clientY - (r.top + r.height / 2)) * 0.45);
        };
        const leave = () => {
          qx(0);
          qy(0);
        };
        btn.addEventListener("mousemove", move);
        btn.addEventListener("mouseleave", leave);
        return () => {
          btn.removeEventListener("mousemove", move);
          btn.removeEventListener("mouseleave", leave);
        };
      },
    );
    return () => mm.revert();
  }, []);

  const isTransparent = !scrolled && !menuOpen;
  const className = [
    "navbar",
    isTransparent ? "navbar--transparent" : "navbar--solid",
    hidden && !menuOpen ? "navbar--hidden" : "",
  ].join(" ");

  return (
    <header className={className} ref={headerRef}>
      {/* ===== MAIN NAVBAR ===== */}
      <div
        className="navbar__backdrop ${menuOpen ? 'is-open' : ''}"
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <div className="container navbar__inner">
        <NavLink to="/" className="navbar__logo">
          <span className="navbar__logo-mark">
            <img src="/lgo.png" alt="Anappuram Furniture Logo" />
          </span>
          <span className="navbar__logo-text">
            Anappuram Furniture
            <small>Premium Wooden Furniture</small>
          </span>
        </NavLink>

        <nav
          className={`navbar__links ${menuOpen ? "navbar__links--open" : ""}`}
        >
          <ul>
            {NAV_LINKS.map((link, i) => (
              <li key={link.to} style={{ "--i": i }}>
                <NavLink
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `navbar__link ${isActive ? "navbar__link--active" : ""}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {link.label}
                      {isActive && (
                        <motion.span
                          layoutId="nav-indicator"
                          className="navbar__indicator"
                          transition={{
                            type: "spring",
                            stiffness: 420,
                            damping: 34,
                          }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
          <a
            href="tel:+919447297179"
            className="btn navbar__call-btn"
            style={{ "--i": NAV_LINKS.length }}
          >
            Call Now
          </a>
        </nav>

        <button
          type="button"
          className={`navbar__hamburger ${menuOpen ? "is-open" : ""}`}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}
